/**
 * Browser Fingerprinting Service for SMART Customer Recognition
 *
 * This service generates a stable fingerprint hash that can identify
 * returning visitors even without login. It's used by the AI to
 * provide personalized recommendations and remember customer context.
 *
 * Components fingerprinted:
 * - Canvas rendering
 * - WebGL renderer info
 * - Audio context
 * - Screen properties
 * - Timezone and language
 * - Platform and user agent
 * - Hardware info
 */

const FINGERPRINT_STORAGE_KEY = 'petmas_fingerprint';
const FINGERPRINT_VERSION = 1;

// Hash algorithm constants (djb2)
const DJB2_INITIAL_HASH = 5381;
const DJB2_SHIFT_BITS = 5;
const HASH_HEX_LENGTH = 8;

// Canvas fingerprint constants
const CANVAS_WIDTH = 200;
const CANVAS_HEIGHT = 50;
const CANVAS_RECT_X = 125;
const CANVAS_RECT_Y = 1;
const CANVAS_RECT_WIDTH = 62;
const CANVAS_RECT_HEIGHT = 20;
const CANVAS_TEXT_X1 = 2;
const CANVAS_TEXT_Y1 = 15;
const CANVAS_TEXT_X2 = 4;
const CANVAS_TEXT_Y2 = 17;
const CANVAS_ARC_X = 50;
const CANVAS_ARC_Y = 50;
const CANVAS_ARC_RADIUS = 50;

// Audio fingerprint constants
const AUDIO_BUFFER_SIZE = 4096;
const AUDIO_CHANNELS_IN = 1;
const AUDIO_CHANNELS_OUT = 1;
const OSCILLATOR_FREQUENCY = 10000;
const AUDIO_TIMEOUT_MS = 1000;

// Extend Window interface for webkit prefixed AudioContext
interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext;
  webkitOfflineAudioContext?: typeof OfflineAudioContext;
}

interface FingerprintData {
  hash: string;
  version: number;
  components: {
    canvas: string;
    webgl: string;
    audio: string;
    screen: string;
    timezone: string;
    language: string;
    platform: string;
    hardwareConcurrency: number;
    deviceMemory: number | undefined;
    touchSupport: boolean;
    colorDepth: number;
    pixelRatio: number;
  };
  generatedAt: number;
}

/**
 * Generate a stable hash from a string using djb2 algorithm
 */
function hashString(str: string): string {
  let hash = DJB2_INITIAL_HASH;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << DJB2_SHIFT_BITS) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer then to hex
  return (hash >>> 0).toString(16).padStart(HASH_HEX_LENGTH, '0');
}

/**
 * Get canvas fingerprint by drawing unique shapes and extracting the data
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Draw text with specific font
    ctx.textBaseline = 'alphabetic';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(CANVAS_RECT_X, CANVAS_RECT_Y, CANVAS_RECT_WIDTH, CANVAS_RECT_HEIGHT);
    ctx.fillStyle = '#069';
    ctx.fillText('PetMas fingerprint', CANVAS_TEXT_X1, CANVAS_TEXT_Y1);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('PetMas fingerprint', CANVAS_TEXT_X2, CANVAS_TEXT_Y2);

    // Draw geometric shapes
    ctx.beginPath();
    ctx.arc(CANVAS_ARC_X, CANVAS_ARC_Y, CANVAS_ARC_RADIUS, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    // Extract and hash the canvas data
    const dataUrl = canvas.toDataURL();
    return hashString(dataUrl);
  } catch {
    return 'canvas-error';
  }
}

/**
 * Get WebGL fingerprint from renderer information
 */
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';

    const webgl = gl as WebGLRenderingContext;
    const debugInfo = webgl.getExtension('WEBGL_debug_renderer_info');

    let vendor = 'unknown';
    let renderer = 'unknown';

    if (debugInfo) {
      vendor = webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? 'unknown';
      renderer = webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? 'unknown';
    }

    return hashString(`${vendor}~${renderer}`);
  } catch {
    return 'webgl-error';
  }
}

/**
 * Get audio context fingerprint
 */
function getAudioFingerprint(): Promise<string> {
  return new Promise((resolve) => {
    try {
      const windowWithWebkit = window as WindowWithWebkitAudio;
      const AudioContextClass = window.AudioContext || windowWithWebkit.webkitAudioContext;
      if (!AudioContextClass) {
        resolve('no-audio');
        return;
      }

      const context = new AudioContextClass();
      const oscillator = context.createOscillator();
      const analyser = context.createAnalyser();
      const gainNode = context.createGain();
      const scriptProcessor = context.createScriptProcessor(AUDIO_BUFFER_SIZE, AUDIO_CHANNELS_IN, AUDIO_CHANNELS_OUT);

      gainNode.gain.value = 0; // Mute output
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(OSCILLATOR_FREQUENCY, context.currentTime);

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(context.destination);

      oscillator.start(0);

      scriptProcessor.onaudioprocess = (event) => {
        const output = event.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < output.length; i++) {
          sum += Math.abs(output[i]);
        }

        oscillator.disconnect();
        scriptProcessor.disconnect();
        gainNode.disconnect();
        context.close();

        resolve(hashString(sum.toString()));
      };

      // Timeout fallback
      setTimeout(() => {
        try {
          oscillator.disconnect();
          scriptProcessor.disconnect();
          gainNode.disconnect();
          context.close();
        } catch {
          // Ignore cleanup errors
        }
        resolve('audio-timeout');
      }, AUDIO_TIMEOUT_MS);
    } catch {
      resolve('audio-error');
    }
  });
}

/**
 * Get screen properties fingerprint
 */
function getScreenFingerprint(): string {
  const { width, height, colorDepth, pixelDepth } = window.screen;
  const { devicePixelRatio } = window;
  const availWidth = window.screen.availWidth;
  const availHeight = window.screen.availHeight;

  return hashString(`${width}x${height}~${colorDepth}~${pixelDepth}~${devicePixelRatio}~${availWidth}x${availHeight}`);
}

/**
 * Get timezone fingerprint
 */
function getTimezoneFingerprint(): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Date().getTimezoneOffset();
  return hashString(`${timezone}~${offset}`);
}

/**
 * Get language fingerprint
 */
function getLanguageFingerprint(): string {
  const language = navigator.language;
  const languages = navigator.languages?.join(',') ?? language;
  return hashString(`${language}~${languages}`);
}

/**
 * Get platform fingerprint
 */
function getPlatformFingerprint(): string {
  const platform = navigator.platform;
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;
  return hashString(`${platform}~${vendor}~${userAgent}`);
}

/**
 * Check for touch support
 */
function getTouchSupport(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Generate a complete browser fingerprint
 */
export async function generateFingerprint(): Promise<FingerprintData> {
  // Check for existing fingerprint
  const existing = getStoredFingerprint();
  if (existing && existing.version === FINGERPRINT_VERSION) {
    return existing;
  }

  // Generate component fingerprints
  const [audioFingerprint] = await Promise.all([
    getAudioFingerprint(),
  ]);

  const components = {
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint(),
    audio: audioFingerprint,
    screen: getScreenFingerprint(),
    timezone: getTimezoneFingerprint(),
    language: getLanguageFingerprint(),
    platform: getPlatformFingerprint(),
    hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
    deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    touchSupport: getTouchSupport(),
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
  };

  // Combine all components into final hash
  const combinedString = [
    components.canvas,
    components.webgl,
    components.audio,
    components.screen,
    components.timezone,
    components.language,
    components.platform,
    components.hardwareConcurrency.toString(),
    components.deviceMemory?.toString() ?? 'unknown',
    components.touchSupport.toString(),
    components.colorDepth.toString(),
    components.pixelRatio.toString(),
  ].join('|');

  const fingerprint: FingerprintData = {
    hash: hashString(combinedString),
    version: FINGERPRINT_VERSION,
    components,
    generatedAt: Date.now(),
  };

  // Store fingerprint
  storeFingerprint(fingerprint);

  return fingerprint;
}

/**
 * Get stored fingerprint from localStorage
 */
function getStoredFingerprint(): FingerprintData | null {
  try {
    const stored = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as FingerprintData;
  } catch {
    return null;
  }
}

/**
 * Store fingerprint in localStorage
 */
function storeFingerprint(fingerprint: FingerprintData): void {
  try {
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, JSON.stringify(fingerprint));
  } catch {
    console.warn('[Fingerprint] Failed to store fingerprint');
  }
}

/**
 * Get the fingerprint hash (shorthand method)
 */
export async function getFingerprintHash(): Promise<string> {
  const fingerprint = await generateFingerprint();
  return fingerprint.hash;
}

/**
 * Get device metadata for analytics
 */
export function getDeviceMetadata(): {
  deviceType: 'desktop' | 'mobile' | 'tablet';
  browser: string;
  os: string;
  screenResolution: string;
  timezone: string;
  language: string;
} {
  const ua = navigator.userAgent.toLowerCase();

  // Detect device type
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    deviceType = 'tablet';
  } else if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    deviceType = 'mobile';
  }

  // Detect browser
  let browser = 'unknown';
  if (ua.includes('firefox')) browser = 'firefox';
  else if (ua.includes('edg')) browser = 'edge';
  else if (ua.includes('chrome')) browser = 'chrome';
  else if (ua.includes('safari')) browser = 'safari';
  else if (ua.includes('opera') || ua.includes('opr')) browser = 'opera';

  // Detect OS
  let os = 'unknown';
  if (ua.includes('win')) os = 'windows';
  else if (ua.includes('mac')) os = 'macos';
  else if (ua.includes('linux')) os = 'linux';
  else if (ua.includes('android')) os = 'android';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'ios';

  return {
    deviceType,
    browser,
    os,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  };
}
